import Axios from 'axios';
import * as Auth from 'aws-amplify/auth';
import mainApi from 'api/main';
import { notificationCreated } from 'reducers/appNotification';
import { CognitoErrors } from 'utils/constants';
import { is400StatusHandled } from 'utils/helpers';
import { navigateTo } from 'utils/navigation';

export const configureAxios = store => {
    const { dispatch } = store;

    Axios.defaults.baseURL = process.env.REACT_APP_API_URL;

    Axios.interceptors.request.use(
        async opt => {
            const { user } = store.getState();
            const { baseURL } = Axios.defaults;
            const requestFullURL = baseURL + opt.url;
            const request = {
                ...opt,
                baseURL: opt.url.includes('/translations')
                    ? requestFullURL.replace('/api/', '/').replace(opt.url, '')
                    : baseURL,
                responseType: 'json',
                headers:
                    opt.method === 'patch'
                        ? { 'Content-Type': 'application/merge-patch+json' }
                        : {},
            };

            try {
                const { tokens } = await Auth.fetchAuthSession({ forceRefresh: true });

                if (tokens?.accessToken) {
                    let stringToken = tokens.accessToken.toString();
                    request.headers.Authorization = `Bearer ${stringToken}`;
                } else {
                    throw new Error(CognitoErrors.NO_USER);
                }

                if (opt.headers['Content-Type']) {
                    request.headers['Content-Type'] = opt.headers['Content-Type'];
                }

                if (opt.responseType) {
                    request.responseType = opt.responseType;
                }

                return request;
            } catch (error) {
                if (error === CognitoErrors.NO_USER && !request.url.includes('logout')) {
                    // /setup-password route is an exception to this rule
                    if (user.internalUser) {
                        dispatch(mainApi.endpoints.signOut.initiate(true));
                    }

                    if (request.url.includes('/me')) throw new Axios.Cancel('No user.');
                }

                return request;
            }
        },
        error => Promise.reject(error)
    );

    Axios.interceptors.response.use(
        response => {
            if (
                response?.config?.url?.includes('download') ||
                response?.config?.url?.includes('/payments/pec') ||
                response?.config?.url?.includes('/simulate') ||
                response?.config?.url?.includes('/export') ||
                (response?.config?.url?.includes('/report') &&
                    !response?.config?.url?.includes('/reporting')) ||
                response?.config?.url?.includes('/portfolio/generate') ||
                (response?.config?.method === 'get' &&
                    (response?.config?.url?.includes('/notifications') ||
                        response?.config?.url?.includes('/referrals') ||
                        response?.config?.url?.includes('/sgps')))
            ) {
                return Promise.resolve(response);
            }
            return Promise.resolve(response.data);
        },
        error => {
            const { user } = store.getState();

            switch (error.response?.status) {
                case 400:
                    if (!is400StatusHandled(error.response)) {
                        dispatch(notificationCreated('ERROR_GENERAL'));
                    }
                    return Promise.reject(error.response);
                case 401:
                    if (user?.internalUser) {
                        dispatch(notificationCreated('ERROR_401'));
                        navigateTo('/sign-in');
                    }
                    return Promise.reject(error.response.data);
                case 403:
                    if (!user?.internalUser?.impersonatedByUser) {
                        dispatch(notificationCreated('ERROR_GENERAL'));
                    }
                    return Promise.reject(error.response.status);
                case 404:
                    if (error?.config?.url?.includes('/documents/download')) {
                        return Promise.reject(error.response);
                    }
                    return Promise.reject(error.response.data);
                case 415:
                    return Promise.reject(error.response);
                case 422:
                    return Promise.reject(error.response);
                case 500:
                    dispatch(notificationCreated('ERROR_GENERAL'));
                    return Promise.reject(error);
                default:
                    return Promise.reject(error.response);
            }
        }
    );
};
